import { useState } from 'react'

// ─── Design tokens ───
const font = '"Geist", system-ui, sans-serif'
const accent = '#D46A3A'
const accentBtn = '#D97645'
const textDark = '#1A1714'
const textMuted = '#8B7D6B'
const textFaint = '#B8A898'
const textCompFree = '#C4B5A5'
const borderRow = '#F0E8DF'
const leftBg = '#EDE4D8'
const rightBg = '#FEFCF9'

// ─── Types ───
export type BillingCycle = 'monthly' | 'yearly'
export type PlanTier = 'pro' | 'business'

interface PaywallModalProps {
  open: boolean
  plan: PlanTier
  onClose: () => void
  onUpgrade?: (plan: PlanTier, cycle: BillingCycle) => void
}

// ─── Plan-specific data ───
const planData = {
  pro: {
    label: 'Pro',
    subtitle: '14-day free trial. No credit card required.',
    pricing: {
      monthly: { price: '12.99', period: '/month', originalPrice: null as string | null },
      yearly: { price: '99', period: '/year', originalPrice: '$155.88' },
    },
    savePercent: '36%',
    heading: 'Finish your work faster.\nNo limits.',
    description: 'Unlimited AI, unlimited pages,\nevery tool — pick up right where you left off.',
    benefits: [
      'Unlimited AI conversations',
      'Up to 10 files per conversation (500 MB)',
      'Unlimited file operations',
      'Unlimited e-signatures + full audit trail',
      'Full conversation history + AI export (PDF)',
      'Priority processing + dedicated support',
    ],
    comparison: [
      { label: 'AI conversations', free: '7/mo', plan: 'Unlimited' },
      { label: 'Files per conversation', free: '3', plan: '10' },
      { label: 'File operations', free: '20/mo', plan: 'Unlimited' },
      { label: 'Priority speed', free: '—', plan: 'check' },
    ],
  },
  business: {
    label: 'Enterprise',
    subtitle: 'Everything in Pro, built for teams.',
    pricing: {
      monthly: { price: '39.99', period: '/month', originalPrice: null as string | null },
      yearly: { price: '299', period: '/year', originalPrice: '$479.88' },
    },
    savePercent: '38%',
    heading: 'Built for teams that\nrun on documents.',
    description: 'Stop passing files back and forth —\nwork together, in one place.',
    benefits: [
      'Everything in Pro',
      'Up to 7 team seats — $2.99/additional',
      'Deal & matter workspaces',
      'Gmail, Drive + Dropbox integrations',
      'Workflow automation + bulk processing',
      'Dedicated onboarding + priority support',
    ],
    comparison: [
      { label: 'Team seats', free: '1', plan: '7' },
      { label: 'Workspaces', free: '—', plan: 'check' },
      { label: 'Integrations', free: '—', plan: 'check' },
      { label: 'Workflow automation', free: '—', plan: 'check' },
    ],
  },
}

// ─── Trusted logos ───
const companyLogos = [
  { name: 'Google', src: '/TrustedforCorner/Google_2015_logo.svg.png', h: 16 },
  { name: 'Microsoft', src: '/TrustedforCorner/Microsoft-logo.webp', h: 16 },
  { name: 'Y Combinator', src: '/TrustedforCorner/Y_Combinator_logo_text_wordmark.png', h: 16 },
  { name: 'Alchemist', src: '/TrustedforCorner/logo-AlchemistAccelerator-top.png', h: 18 },
]

const schoolLogos = [
  { name: 'Harvard', src: '/TrustedforCorner/Harvard_University_coat_of_arms.svg', h: 22 },
  { name: 'Stanford', src: '/TrustedforCorner/stanford-university.webp', h: 22 },
  { name: 'Cornell', src: '/TrustedforCorner/Cornell_University_seal.svg.png', h: 22 },
  { name: 'CMU', src: '/TrustedforCorner/CMU_Logo_Stack_Red (1).png', h: 22 },
  { name: 'UPenn', src: '/TrustedforCorner/UPenn_shield_with_banner.svg.webp', h: 22 },
  { name: 'MIT', src: '/TrustedforCorner/MIT_logo.png', h: 20 },
]

// ─── Corner Logo SVG ───
function CornerLogo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <svg width={22} height={22} viewBox="0 0 256 256" fill="none">
        <defs>
          <linearGradient id="pwLogoGrad" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
            <stop stopColor="#D46A3A" />
            <stop offset="1" stopColor="#8B3A10" />
          </linearGradient>
        </defs>
        <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#pwLogoGrad)" strokeWidth="40" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: 16, fontWeight: 600, color: textDark, fontFamily: font, letterSpacing: '-0.01em', lineHeight: '20px' }}>Corner</span>
    </div>
  )
}

function CheckCircle() {
  return (
    <div style={{
      width: 20, height: 20, borderRadius: '50%',
      backgroundColor: '#D46A3A1F',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M2 5.5L4 7.5L8 3" stroke={accent} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
}

function TableCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3 7.5L5.5 10L11 4" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ═══════════════════════════════════════════════
// PAYWALL MODAL
// ═══════════════════════════════════════════════

export default function PaywallModal({ open, plan, onClose, onUpgrade }: PaywallModalProps) {
  const [cycle, setCycle] = useState<BillingCycle>('yearly')
  const data = planData[plan]
  const p = data.pricing[cycle]

  if (!open) return null

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#1A171473',
        backdropFilter: 'blur(60px)', WebkitBackdropFilter: 'blur(60px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          display: 'flex', width: 860, borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '#00000073 0px 32px 80px',
          fontFamily: font,
        }}
      >
        {/* ══════ LEFT PANEL ══════ */}
        <div style={{
          width: 380, flexShrink: 0, display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between', position: 'relative',
          padding: '44px 40px', backgroundColor: leftBg, overflow: 'hidden',
        }}>
          {/* Decorative radial blobs */}
          <div style={{
            position: 'absolute', top: -60, right: -60, width: 240, height: 240,
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(212, 106, 58, 0.18) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', bottom: -40, left: -40, width: 180, height: 180,
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(212, 106, 58, 0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          {/* Content */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32, position: 'relative', zIndex: 1 }}>
            <CornerLogo />

            <div>
              <h2 style={{
                fontSize: 26, fontWeight: 700, lineHeight: '32px',
                letterSpacing: '-0.03em', color: textDark, margin: 0,
                whiteSpace: 'pre-wrap', fontFamily: font,
              }}>
                {data.heading}
              </h2>
              <p style={{
                fontSize: 13, fontWeight: 400, lineHeight: '160%',
                color: textMuted, margin: '10px 0 0', fontFamily: font,
                whiteSpace: 'pre-wrap',
              }}>
                {data.description}
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {data.benefits.map(b => (
                <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <CheckCircle />
                  <span style={{ fontSize: 13, fontWeight: 400, color: textDark, fontFamily: font, lineHeight: '16px' }}>{b}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trusted logos */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, position: 'relative', zIndex: 1, paddingRight: 32, paddingBottom: 32, paddingLeft: 32, marginLeft: -32, marginRight: -32, marginBottom: -32 }}>
            <span style={{ fontSize: 11, fontWeight: 400, letterSpacing: '0.04em', textTransform: 'uppercase', color: textMuted, lineHeight: '14px', fontFamily: font }}>
              Trusted by people at
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: 340 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, height: 20 }}>
                {companyLogos.map(l => (
                  <img key={l.name} src={l.src} alt={l.name} style={{ height: l.h, objectFit: 'contain', opacity: 0.8 }} />
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, height: 24 }}>
                {schoolLogos.map(l => (
                  <img key={l.name} src={l.src} alt={l.name} style={{ height: l.h, objectFit: 'contain', opacity: 0.8 }} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════ RIGHT PANEL ══════ */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '40px 36px 32px', backgroundColor: rightBg,
        }}>
          {/* Close button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
            <button
              onClick={onClose}
              style={{
                border: 'none', background: 'none',
                cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#C4B5A5" strokeWidth="1.8" strokeLinecap="round">
                <path d="M12 4L4 12M4 4l8 8" />
              </svg>
            </button>
          </div>

          {/* Main content */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Title */}
            <div>
              <h3 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: textDark, margin: 0, fontFamily: font, lineHeight: '24px' }}>
                Upgrade to {data.label}
              </h3>
              <p style={{ fontSize: 13, fontWeight: 400, color: textFaint, margin: '4px 0 0', fontFamily: font, lineHeight: '16px' }}>
                {data.subtitle}
              </p>
            </div>

            {/* Billing toggle */}
            <div style={{
              display: 'flex', alignItems: 'center', borderRadius: 10,
              padding: 3, backgroundColor: '#F5F0EB',
            }}>
              <button
                onClick={() => setCycle('monthly')}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 8, padding: '7px 0', border: 'none',
                  cursor: 'pointer', fontFamily: font,
                  fontSize: cycle === 'monthly' ? 12 : 16,
                  lineHeight: cycle === 'monthly' ? '16px' : '20px',
                  fontWeight: cycle === 'monthly' ? 600 : 400,
                  color: cycle === 'monthly' ? textDark : '#000', textAlign: 'center',
                  backgroundColor: cycle === 'monthly' ? '#FFFFFF' : 'transparent',
                  boxShadow: cycle === 'monthly' ? '#00000014 0px 1px 3px' : 'none',
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setCycle('yearly')}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 8, padding: '7px 0', border: 'none', gap: 6,
                  cursor: 'pointer', fontFamily: font,
                  fontSize: cycle === 'yearly' ? 12 : 16,
                  lineHeight: cycle === 'yearly' ? '16px' : '20px',
                  fontWeight: cycle === 'yearly' ? 600 : 400,
                  color: cycle === 'yearly' ? textDark : '#000', textAlign: 'center',
                  backgroundColor: cycle === 'yearly' ? '#FFFFFF' : 'transparent',
                  boxShadow: cycle === 'yearly' ? '#00000014 0px 1px 3px' : 'none',
                }}
              >
                Yearly
                <span style={{
                  fontSize: 9, fontWeight: 700, color: '#FFFFFF', lineHeight: '12px',
                  backgroundColor: accentBtn, borderRadius: 999, padding: '1px 6px',
                }}>
                  SAVE {data.savePercent}
                </span>
              </button>
            </div>

            {/* Price */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-0.04em', color: textDark, fontFamily: font, lineHeight: '54px', flexShrink: 0 }}>
                ${p.price}
              </span>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 13, fontWeight: 400, color: textFaint, fontFamily: font, lineHeight: '16px' }}>
                  {p.period}
                </span>
                {p.originalPrice && (
                  <span style={{ fontSize: 11, fontWeight: 400, color: '#A09070', fontFamily: font, lineHeight: '14px', textDecoration: 'line-through' }}>
                    {p.originalPrice}
                  </span>
                )}
              </div>
            </div>

            {/* Comparison table */}
            <div>
              {/* Column headers */}
              <div style={{
                display: 'flex', justifyContent: 'flex-end', paddingRight: 14,
                marginBottom: 4, gap: 0,
              }}>
                <span style={{ width: 40, textAlign: 'center', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', color: textCompFree, fontFamily: font, lineHeight: '12px' }}>FREE</span>
                <span style={{ width: 60, textAlign: 'center', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', color: accent, fontFamily: font, lineHeight: '12px' }}>{data.label.toUpperCase()}</span>
              </div>

              {/* Rows */}
              <div style={{
                display: 'flex', flexDirection: 'column',
                borderRadius: 10, overflow: 'hidden',
                border: '1px solid #E4DDD4',
              }}>
                {data.comparison.map((row, i) => (
                  <div key={row.label} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px',
                    backgroundColor: i === 2 ? '#FDFAF7' : 'transparent',
                    borderBottom: i < data.comparison.length - 1 ? `1px solid ${borderRow}` : 'none',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 400, color: '#6B5B4E', fontFamily: font, lineHeight: '16px' }}>
                      {row.label}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                      <span style={{ width: 40, textAlign: 'center', fontSize: 11, fontWeight: 400, color: textCompFree, fontFamily: font, lineHeight: '14px' }}>
                        {row.free}
                      </span>
                      <span style={{ width: 60, textAlign: 'center', fontSize: 11, fontWeight: 600, color: accent, fontFamily: font, lineHeight: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {row.plan === 'check' ? <TableCheck /> : row.plan}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
            <button
              onClick={() => onUpgrade?.(plan, cycle)}
              style={{
                width: '100%', borderRadius: 10, padding: 14, border: 'none',
                backgroundColor: accentBtn, cursor: 'pointer',
                boxShadow: '#D976454D 0px 4px 16px',
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 400, color: '#000', fontFamily: font, lineHeight: '20px' }}>
                Upgrade to {data.label} →
              </span>
            </button>
            <button
              onClick={onClose}
              style={{
                width: '100%', background: 'none', border: 'none',
                padding: '4px 0', cursor: 'pointer', textAlign: 'center' as const,
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 400, color: '#000', fontFamily: font, lineHeight: '20px', textAlign: 'center' }}>
                Continue on Free
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
